import * as Sentry from "@sentry/react";

const isLocalhost = Boolean(
	window.location.hostname === "localhost" ||
	// [::1] is the IPv6 localhost address.
	window.location.hostname === "[::1]" ||
	// 127.0.0.0/8 are considered localhost for IPv4.
	window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
);

// Bound on auto-recovery reloads so a permanently broken SW URL on localhost
// can't loop the tab forever.
const SW_RELOAD_GUARD_KEY = "__sw_reload_guard__";

export function register(): Promise<ServiceWorkerRegistration | undefined> {
	if (!("serviceWorker" in navigator) || process.env.PUBLIC_URL === undefined) {
		return Promise.resolve(undefined);
	}
	const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
	if (publicUrl.origin !== window.location.origin) {
		console.error("Can't init SW: Wrong origin");
		return Promise.resolve(undefined);
	}

	const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

	const start = async (): Promise<ServiceWorkerRegistration | undefined> => {
		try {
			if (isLocalhost) {
				const ok = await checkValidServiceWorker(swUrl);
				if (!ok) return undefined;
			}
			return await navigator.serviceWorker.register(swUrl, {
				scope: "/",
				updateViaCache: "none",
			});
		} catch (err) {
			console.error("Error during service worker registration:", err);
			Sentry.captureException(err);
			return undefined;
		}
	};

	// If the bundle parses after `load` already fired, an addEventListener('load')
	// would never run. Start immediately in that case.
	if (document.readyState === "complete") {
		return start();
	}
	return new Promise(resolve => {
		window.addEventListener("load", () => {
			start().then(resolve);
		});
	});
}

async function checkValidServiceWorker(swUrl: string): Promise<boolean> {
	try {
		const response = await fetch(swUrl, { headers: { "Service-Worker": "script" } });
		const contentType = response.headers.get("content-type");
		if (response.status === 404 || (contentType != null && contentType.indexOf("javascript") === -1)) {
			console.log("No service worker found at ", swUrl);
			const alreadyTried = sessionStorage.getItem(SW_RELOAD_GUARD_KEY) === "1";
			const reg = await navigator.serviceWorker.getRegistration("/");
			await reg?.unregister();
			if (!alreadyTried) {
				sessionStorage.setItem(SW_RELOAD_GUARD_KEY, "1");
				window.location.reload();
			} else {
				console.warn("SW recovery reload already attempted this session — giving up");
			}
			return false;
		}
		sessionStorage.removeItem(SW_RELOAD_GUARD_KEY);
		return true;
	} catch (err) {
		console.log("No internet connection found. App is running in offline mode.");
		Sentry.captureException(err);
		return false;
	}
}

export function unregister() {
	if ("serviceWorker" in navigator) {
		navigator.serviceWorker.ready
			.then(registration => registration.unregister())
			.catch(error => {
				console.error(error.message);
				Sentry.captureException(error);
			});
	}
}
